import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import reelbg from '../../assets/img/reelbg.png';
import conceptImg from '../../assets/img/create.png';
import blueprintImg from '../../assets/img/blueprint.png';
import storyboardImg from '../../assets/img/storyboard.png';
import editImg from '../../assets/img/edit.png';
import shareImg from '../../assets/img/create.png';
import fixedVideo from '../../assets/fixedvideo.mp4';

function Hero() {
	const [activeTab, setActiveTab] = useState('Concept');
	// Initialize with responsive width based on screen size
	const [videoWidth, setVideoWidth] = useState(() => {
		if (typeof window !== 'undefined') {
			const width = window.innerWidth;
			if (width < 640) return 120; // Mobile
			if (width < 1024) return 160; // Tablet
			return 230; // Desktop
		}
		return 230;
	});

	useEffect(() => {
		const handleScroll = () => {
			const scrollPosition = window.scrollY;
			const windowWidth = window.innerWidth;
			
			// Determine base sizes based on screen size
			let maxWidth, minWidth;
			if (windowWidth < 640) {
				// Mobile: 120px to 100px
				maxWidth = 120;
				minWidth = 100;
			} else if (windowWidth < 1024) {
				// Tablet: 160px to 140px
				maxWidth = 160;
				minWidth = 140;
			} else {
				// Desktop: 230px to 190px
				maxWidth = 230;
				minWidth = 190;
			}

			// Start shrinking after 100px scroll, fully shrink at 200px scroll
			if (scrollPosition < 100) {
				setVideoWidth(maxWidth);
			} else if (scrollPosition > 200) {
				setVideoWidth(minWidth);
			} else {
				// Linear interpolation between maxWidth and minWidth
				const progress = (scrollPosition - 100) / 100; // 0 to 1
				const newWidth = maxWidth - (progress * (maxWidth - minWidth));
				setVideoWidth(newWidth);
			}
		};

		window.addEventListener('scroll', handleScroll);
		window.addEventListener('resize', handleScroll);
		// Set initial width
		handleScroll();

		return () => {
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', handleScroll);
		};
	}, []);

	const tabData = {
		'Concept': {
			title: 'No video editing skills required',
			description: 'Start from a document, idea or blank canvas. Use simple text prompts to clarify your concept',
			image: conceptImg,
		},
		'Blueprint': {
			title: 'Refine your concept',
			description: 'Answer a dynamic super prompted questionnaire adapted to your content and context to create scenes and a script suited for your needs',
			image: blueprintImg,
		},
		'Storyboard': {
			title: 'Direct your Reel Report',
			description: 'Review the Ai generated storyboard, make edits to the avatar, text, infographics, charts  in real-time',
			image: storyboardImg,
		},
		'Edit': {
			title: 'Refine and edit',
			description: 'Make video edits to finalize the video for production',
			image: editImg,
		},
		'Share': {
			title: 'Publish and Distribute',
			description: 'Export, or send your finished video anywhere instantly',
			image: conceptImg,
		},
	};
	return (
		<>
			{/* Fixed Autoplay Video */}
			<div className="fixed bottom-3 right-3 sm:bottom-4 sm:right-4 lg:bottom-6 lg:right-6 z-40">
				<video
					autoPlay
					loop
					muted
					playsInline
					className="rounded-[1.5rem] shadow-2xl transition-all duration-300 ease-in-out"
					style={{
						width: `${videoWidth}px`,
						objectFit: 'contain',
					}}
				>
					<source src={fixedVideo} type="video/mp4" />
					Your browser does not support the video tag.
				</video>
			</div>

			<section className="relative section-spacing" style={{fontFamily:'var(--brand-font)', backgroundImage:`url(${reelbg})`, backgroundSize:'cover', backgroundPosition:'center', backgroundRepeat:'no-repeat'}}>
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="text-center">
					<motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="inline-flex items-center rounded-full px-2 py-1 sm:px-3 text-xs sm:text-sm md:text-md font-medium text-[#1470D2] border border-[#1470D2] shadow-sm" >The simplest TEXT to VIDEO platform for business</motion.div>
					<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="max-w-6xl mx-auto mt-4 sm:mt-6 text-3xl md:text-5xl  font-extrabold tracking-tight text-gray-900 px-2">
					Communication, brought to life, within branding guidelines <br className="hidden sm:block" /> <span className="text-[#1470D2] mt-1">and under your control</span>
					</motion.div>
					<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="max-w-2xl mx-auto mt-4 sm:mt-6 text-sm md:text-base tracking-tight text-gray-900 px-2">
					Summarize the doc in a brand aligned video with avatars, infographics, charts and
					voiceover and save upto 90% of time and cost on video production					</motion.div>
					
					<motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.2 }} className="mt-6 sm:mt-8 md:mt-10 flex items-center justify-center gap-4">
						<a href="/book-demo" className="inline-flex items-center rounded-md px-5 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-white shadow-sm" style={{
							backgroundImage:'linear-gradient(90deg, rgb(20, 112, 210) 0%, rgb(142, 35, 192) 100%)'
						}}>Request a Demo invite</a>
					</motion.div>
				</div>
			</div>
		</section>
		
		{/* Tabs Section */}
		<section className="relative section-spacing bg-gradient-to-b from-gray-50/50 to-white" style={{fontFamily:'var(--brand-font)'}}>
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				{/* Mobile & Tablet: Scrollable container */}
				<div className="flex justify-center lg:justify-center">
					<div 
						className="overflow-x-auto lg:overflow-x-visible pb-3 lg:pb-0 w-full lg:w-auto" 
						style={{
							scrollbarWidth: 'none',
							msOverflowStyle: 'none',
						}}
					>
						<style>{`
							.tabs-container::-webkit-scrollbar {
								display: none;
							}
						`}</style>
						<div className="tabs-container inline-flex items-center gap-1.5 sm:gap-2 rounded-full p-1.5 sm:p-2 border-2 border-gray-300 bg-white shadow-lg lg:shadow-lg min-w-fit">
							{Object.keys(tabData).map((tab) => (
								<motion.button 
									whileTap={{ scale: 0.98 }} 
									whileHover={{ scale: 1.02 }} 
									key={tab} 
									onClick={() => setActiveTab(tab)} 
									className={`px-5 py-2 sm:px-6 md:px-7 lg:px-8 sm:py-2.5 rounded-full text-sm sm:text-base font-semibold transition-all duration-300 whitespace-nowrap ${
										activeTab === tab 
											? 'text-white shadow-md' 
											: 'text-gray-700 hover:bg-gray-50'
										}`} 
									style={{
										backgroundImage: activeTab === tab ? 'linear-gradient(90deg, rgb(20, 112, 210) 0%, rgb(142, 35, 192) 100%)' : 'none',
									}}
								>
									{tab}
								</motion.button>
							))}
						</div>
					</div>
				</div>

			<div className="mt-6 sm:mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 items-center">
				<motion.div key={activeTab} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35 }}>
					<h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">{tabData[activeTab].title}</h3>
					<p className="text-gray-600 text-base sm:text-lg leading-relaxed max-w-xl">{tabData[activeTab].description}</p>
				</motion.div>
				<motion.div key={activeTab + '-img'} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35 }} className="relative">
					<div className="rounded-xl overflow-hidden shadow-lg ring-1 ring-black/5 bg-white">
						<img src={tabData[activeTab].image} alt={`${activeTab} preview`} className="w-full h-auto object-cover" />	
					</div>
				</motion.div>
			</div>
			
		</div>
		</section>
		</>
	);
}

export default Hero;


